import { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { classAPI } from '../utils/api';
import TeacherMaterials from '../components/Teacher/TeacherMaterials';
import TeacherResponses from '../components/Teacher/TeacherResponses';
import {
  RiBookOpenLine, RiBarChartLine, RiLogoutBoxLine,
  RiMenuLine, RiCloseLine, RiUserLine,
} from 'react-icons/ri';

const menuItems = [
  { id: 'materials',  label: 'Study Materials',    icon: RiBookOpenLine, path: '/teacher' },
  { id: 'responses',  label: 'Student Responses',  icon: RiBarChartLine, path: '/teacher/responses' },
];

const TeacherDashboard = () => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const response = await classAPI.getAllClasses();
        setClasses(response.data.classes || []);
      } catch (error) {
        console.error('Error fetching classes:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchClasses();
  }, []);

  const handleLogout = () => { logout(); navigate('/login'); };

  const active = menuItems.find(m => {
    if (m.path === '/teacher') return location.pathname === '/teacher' || location.pathname === '/teacher/';
    return location.pathname.startsWith(m.path);
  })?.id || 'materials';

  const Sidebar = ({ mobile = false }) => (
    <aside className="flex flex-col h-full bg-slate-900">
      {/* Brand */}
      <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-800">
        <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <RiBookOpenLine className="w-4 h-4 text-white" />
        </div>
        <div>
          <p className="text-white font-bold text-sm leading-tight">Demo Publication</p>
          <p className="text-slate-500 text-xs">Teacher Portal</p>
        </div>
        {mobile && (
          <button onClick={() => setSidebarOpen(false)} className="ml-auto text-slate-400 hover:text-white">
            <RiCloseLine className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {menuItems.map(({ id, label, icon: Icon, path }) => (
          <Link
            key={id}
            to={path}
            onClick={() => setSidebarOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
              active === id
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
            }`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            {label}
          </Link>
        ))}
      </nav>

      {/* User */}
      <div className="px-3 py-4 border-t border-slate-800">
        <div className="flex items-center gap-3 px-3 py-2 mb-2">
          <div className="w-7 h-7 bg-emerald-700 rounded-full flex items-center justify-center flex-shrink-0">
            <RiUserLine className="w-3.5 h-3.5 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-slate-300 text-xs font-medium truncate">{auth.user?.name}</p>
            <p className="text-slate-600 text-xs">{auth.user?.teacherId}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-slate-400 hover:bg-red-950 hover:text-red-400 transition-all duration-150"
        >
          <RiLogoutBoxLine className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-slate-100 overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex flex-col w-60 flex-shrink-0 border-r border-slate-200 shadow-sm">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="relative w-64 flex flex-col shadow-2xl">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {/* Topbar */}
        <header className="bg-white border-b border-slate-200 px-5 py-3.5 flex items-center justify-between flex-shrink-0 shadow-sm">
          <div className="flex items-center gap-3">
            <button
              className="md:hidden p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <RiMenuLine className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-base font-semibold text-slate-800">
                {menuItems.find(m => m.id === active)?.label || 'Study Materials'}
              </h1>
              <p className="text-xs text-slate-400 hidden sm:block">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
          </div>
        </header>

        {/* Page */}
        <main className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 animate-spin rounded-full border-2 border-slate-200 border-t-emerald-600" />
            </div>
          ) : (
            <Routes>
              <Route path="/"          element={<TeacherMaterials classes={classes} />} />
              <Route path="/responses" element={<TeacherResponses />} />
            </Routes>
          )}
        </main>
      </div>
    </div>
  );
};

export default TeacherDashboard;
